import {
  ArchiveBoxIcon,
  BookOpenIcon,
  ChevronDownIcon,
  DocumentTextIcon,
  HandThumbUpIcon,
  NoSymbolIcon,
} from '@heroicons/react/24/outline';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { type GetCommentParams, getPartnerProposalComments, postComment } from '../../../../../api/comments';
import { archivePartnerProposal, getPartnerProposal, patchPartnerProposal } from '../../../../../api/Rover/partnerProposal';
import { developerApplicationStatus } from '../../../../../constants/DeveloperApplication';
import { usePermission } from '../../../../../hooks/usePermission';
import { notifyUser } from '../../../../../store/Toast';
import { convertEnumToText } from '../../../../../utils';
import Comments from '../../../../Comments';
import { HasPermission } from '../../../../Permission/HasPermission';
import DropDown, { type DropDownMenuItem } from '../../../../UI/DropDown';
import Confirm from '../../../../UI/Modal/Confirm';
import ApplicationTimeline from '../../ApplicationTimeline';
import Styles from '../EnterpriseLBADevelopers.module.css';
import ApplicantInformationDetail from './ApplicantInformationDetail';
import ExperienceDetail from './ExperienceDetail';
import LeadershipInformationDetail from './LeadershipInformationDetail';
import ProjectDetail from './ProjectDetail';

export default function DeveloperApplicant() {
  const { id: applicationId } = useParams<string>();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { hasPermission } = usePermission();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  //Tanstack: version 5 query implementation
  const fetchPartnerProposal = useQuery({
    queryKey: ['get-partner-proposal'],
    queryFn: () => getPartnerProposal({ applicationId: Number(applicationId) }),
    staleTime: 5000,
  });
  const partnerProposal = fetchPartnerProposal?.data?.data;
  const postComments = useCallback(
    async (values: { comment: string }) => {
      const response = await postComment({
        data: {
          resource_id: Number(applicationId),
          resource_type: 'PartnerProposal',
          ...values,
        },
      });
      return response;
    },
    [applicationId]
  );

  const getComments = useCallback(
    (_params: GetCommentParams) => {
      return getPartnerProposalComments({
        params: {
          resource_id: Number(applicationId),
          resource_type: 'PartnerProposal',
        },
      });
    },
    [applicationId]
  );

  const changeApplicationStatus = useMutation(
    (data: { status: string }) =>
      patchPartnerProposal({
        data,
        applicationId: Number(applicationId),
      }),

    {
      onSuccess: () => {
        //Tanstack: Invalidate queries after mutation
        queryClient.invalidateQueries({ queryKey: ['get-partner-proposal'] });
        queryClient.invalidateQueries({ queryKey: ['PartnerProposal-application-audits'] });
      },
    }
  );

  const statusChangeHandler = (status: string) => {
    changeApplicationStatus.mutate({ status });
  };

  const statusChangeList: DropDownMenuItem[] = [
    {
      name: 'Under Review',
      clicked: () => statusChangeHandler(developerApplicationStatus.UNDER_REVIEW),
      icon: <BookOpenIcon className='h-5 w-5' />,
    },
    {
      name: 'Document Requested',
      clicked: () => statusChangeHandler(developerApplicationStatus.DOCUMENT_REQUESTED),
      icon: <DocumentTextIcon className='h-5 w-5' />,
    },
    {
      name: 'Deny',
      clicked: () => statusChangeHandler(developerApplicationStatus.DENIED),
      icon: <NoSymbolIcon className='h-5 w-5' />,
    },
    {
      name: 'Approve',
      clicked: () => statusChangeHandler(developerApplicationStatus.APPROVED),
      icon: <HandThumbUpIcon className='h-5 w-5' />,
    },
  ];

  const partnerProposalArchive = useMutation(() => archivePartnerProposal(Number(applicationId)), {
    onSuccess: () => {
      //Tanstack: Invalidate queries after mutation
      queryClient.invalidateQueries({ queryKey: ['get-partner-proposals'] });
      navigate('/app/rover/partner-proposals', { replace: true });
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Partner Proposal Archive successful',
          toastType: 'success',
        })
      );
    },
    onError: () => {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Partner Proposal Archive failed',
          toastType: 'failed',
        })
      );
    },
  });

  const archivePartnerProposalHandler = () => {
    setShowDeleteConfirm(true);
  };

  const confirmArchivePartnerProposal = async () => {
    setShowDeleteConfirm(false);
    partnerProposalArchive.mutate();
  };

  const cancelArchivePartnerProposal = () => {
    setShowDeleteConfirm(false);
  };

  return (
    <>
      {showDeleteConfirm && (
        <Confirm
          title='Archive Partner Proposal'
          message={`You are about to archive Partner Proposal form ${partnerProposal.formData.applicantInformation.entityName}. Are you sure you want to archive ?`}
          acceptButtonLabel='Archive'
          declineButtonLabel='Cancel'
          accept={confirmArchivePartnerProposal}
          decline={cancelArchivePartnerProposal}
        />
      )}
      <div className='min-h-full max-w-screen-2xl mx-auto'>
        {fetchPartnerProposal.isSuccess && (
          <main>
            {/* Page header */}
            <div className='w-full mx-auto px-4 sm:px-6 flex justify-between items-center md:justify-end md:space-x-5 lg:px-8'>
              <div className='flex flex-col-reverse justify-stretch space-y-4 space-y-reverse sm:flex-row-reverse sm:justify-end sm:space-x-reverse sm:space-y-0 sm:space-x-3 md:mt-0 md:flex-row md:space-x-3 z-10'>
                {hasPermission(['UPDATE_PARTNER_PROPOSAL']) && (
                  <DropDown menuItems={statusChangeList}>
                    <div className={`${Styles[partnerProposal.status!]}`}>
                      <span className='font-semibold capitalize'>{convertEnumToText(partnerProposal.status)}</span>
                      <ChevronDownIcon className='h-4 w-4' />
                    </div>
                  </DropDown>
                )}
                {!hasPermission(['UPDATE_PARTNER_PROPOSAL']) && (
                  <div className={`${Styles[partnerProposal.status!]}`}>
                    <span className='font-semibold capitalize'>{convertEnumToText(partnerProposal.status)}</span>
                    <ChevronDownIcon className='h-4 w-4' />
                  </div>
                )}
              </div>
              <HasPermission of='ARCHIVE_PARTNER_PROPOSAL'>
                <button
                  className='flex gap-1 rounded-full bg-tertiaryColor-normal px-4 py-2 text-white font-semibold hover:bg-tertiaryColor-medium cursor-pointer'
                  onClick={archivePartnerProposalHandler}
                >
                  <ArchiveBoxIcon className='h-5 w-5' />
                  <div className='hidden md:block'>Archive</div>
                </button>
              </HasPermission>
            </div>

            <div className='sm:px-6 mt-8 w-full mx-auto grid grid-cols-12 gap-6 '>
              {partnerProposal && (
                <div className='space-y-6 col-span-12 xl:col-span-8'>
                  {/* Description list*/}
                  <ProjectDetail partnerProposal={partnerProposal} />
                  <ApplicantInformationDetail partnerProposal={partnerProposal} />
                  <LeadershipInformationDetail partnerProposal={partnerProposal} />
                  <ExperienceDetail partnerProposal={partnerProposal} />
                  <Comments getComments={getComments} postComments={postComments} queryKey={`${applicationId}-partner-proposal-comments`} />
                </div>
              )}
              <div className='col-span-12 xl:col-span-4 h-screen/2'>
                <ApplicationTimeline activity={partnerProposal} resourceType='PartnerProposal' />
              </div>
            </div>
          </main>
        )}
      </div>
    </>
  );
}
